import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Fuse from 'fuse.js'
import { I } from './Icons'

const Wrap = styled.div`
  position: relative;
`

const PillSelect = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  color: ${p => p.theme.colors.semantic.textPrimary};
  font-size: 13px;
  font-weight: 500;
  max-width: 280px;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
    flex-shrink: 0;
  }
`

const Cap = styled.span`
  color: ${p => p.theme.colors.semantic.textMuted};
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
  flex-shrink: 0;
`

const Val = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: min(360px, 92vw);
  max-height: 360px;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  box-shadow: ${p => p.theme.colors.semantic.shadowLg};
  z-index: 200;
  overflow: hidden;
`

const Search = styled.input`
  border: none;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  padding: 10px 12px;
  font-size: 13px;
  outline: none;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  &::placeholder {
    color: ${p => p.theme.colors.semantic.textMuted};
  }
`

const List = styled.div`
  overflow-y: auto;
  max-height: 300px;
`

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => (p.$active ? p.theme.colors.semantic.surfaceMuted : 'transparent')};
  cursor: pointer;
  font-size: 13px;
  color: ${p => p.theme.colors.semantic.textPrimary};
  &:hover {
    background: ${p => p.theme.colors.semantic.lilacTint};
  }
`

const Code = styled.span`
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => p.theme.colors.semantic.textMuted};
  flex-shrink: 0;
`

const FUSE_KEYS = ['projectCode', 'name']

const WipAiMatterSelect = ({ projects = [], selectedProject, onSelectProject }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const getFiltered = useCallback(() => {
    const list = projects || []
    const q = query.trim()
    if (!q) return list
    const fuse = new Fuse(list, {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 200,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: FUSE_KEYS,
    })
    const hits = fuse.search(q)
    return hits.map(h => (h.item !== undefined ? h.item : h))
  }, [projects, query])

  const filtered = getFiltered()
  const label = selectedProject?.name || 'Select matter'

  return (
    <Wrap ref={wrapRef}>
      <PillSelect
        type="button"
        title="Select matter"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <I.briefcase />
        <Cap>Matter</Cap>
        <Val>{label}</Val>
        <I.chev />
      </PillSelect>
      {open && (
        <Dropdown role="listbox" aria-label="Matters">
          <Search
            autoFocus
            placeholder="Search by name or code…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setOpen(false)
            }}
          />
          <List>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', fontSize: 13, color: '#676767' }}>No matches</div>
            )}
            {filtered.map(p => {
              const active = selectedProject && String(selectedProject._id) === String(p._id)
              return (
                <Row
                  key={p._id}
                  type="button"
                  $active={active}
                  onClick={() => {
                    onSelectProject(p)
                    setOpen(false)
                  }}
                >
                  <Code>{p.projectCode || '—'}</Code>
                  <span style={{ minWidth: 0 }}>{p.name}</span>
                </Row>
              )
            })}
          </List>
        </Dropdown>
      )}
    </Wrap>
  )
}

WipAiMatterSelect.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  selectedProject: PropTypes.object,
  onSelectProject: PropTypes.func.isRequired,
}

export default WipAiMatterSelect
